import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  InputAdornment,
  Divider,
  MenuItem,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Search as SearchIcon,
  CreditCard as NotaCreditoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import tiposDocumentos from 'utils/tiposDocumentos.json';
import { useProductosMaterialContext } from 'context';
import { FP, fpInputSx } from 'components/financepro';

/**
 * Diálogo de pago de factura: combina múltiples métodos + nota de crédito.
 * El permiso de modificación se valida en la pantalla padre (FacturaPOS).
 */
const PagoFactura = ({
  open,
  total,
  onClose,
  onConfirm,
  puedeModificar = true,
  formasPago = [],
}) => {
  const { controller, selectedCompany, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const metodosUnicos = useMemo(() => {
    const desdeCatalogo = (formasPago || [])
      .map((fp) => {
        const documento = String(fp.documento || '').trim();
        if (!documento) return null;
        return {
          codigo: documento,
          nombre: documento,
          original: documento,
          cuenta_contable: String(fp.cuenta_contable || '').trim(),
          necesita_referencia: String(fp.necesita_referencia || 'N').toUpperCase() === 'S',
        };
      })
      .filter(Boolean);
    if (desdeCatalogo.length > 0) {
      return [
        ...desdeCatalogo,
        {
          codigo: 'OTROS',
          nombre: 'Otros (combinar métodos)',
          original: 'Otros',
          cuenta_contable: '',
          necesita_referencia: false,
        },
      ];
    }
    const metodosPagoJSON = tiposDocumentos.find((item) => item.metodospagos)?.metodospagos || [];
    const metodosPagoNormalizados = metodosPagoJSON.map((metodo) => {
      const metodoUpper = metodo.toUpperCase();
      if (metodoUpper.includes('TARJETA')) {
        return { codigo: 'TARJETA', nombre: 'Tarjeta de crédito / débito', original: metodo };
      }
      if (metodoUpper.includes('EFECTIVO')) {
        return { codigo: 'EFECTIVO', nombre: 'Efectivo', original: metodo };
      }
      if (metodoUpper.includes('TRANSFERENCIA')) {
        return { codigo: 'TRANSFERENCIA', nombre: 'Transferencia', original: metodo };
      }
      if (metodoUpper.includes('CHEQUE')) {
        return { codigo: 'CHEQUE', nombre: 'Cheque', original: metodo };
      }
      if (metodoUpper.includes('OTROS')) {
        return { codigo: 'OTROS', nombre: 'Otros (combinar métodos)', original: metodo };
      }
      const codigo = metodoUpper.replace(/\s+/g, '_');
      return { codigo, nombre: metodo, original: metodo };
    });
    return metodosPagoNormalizados.reduce((acc, metodo) => {
      if (!acc.find((m) => m.codigo === metodo.codigo)) acc.push(metodo);
      return acc;
    }, []);
  }, [formasPago]);

  const [metodoPago, setMetodoPago] = useState(metodosUnicos[0]?.codigo || 'EFECTIVO');
  const [montos, setMontos] = useState({});
  const [referencias, setReferencias] = useState({});
  const [error, setError] = useState('');
  const [tipoBusquedaNC, setTipoBusquedaNC] = useState('ncf');
  const [valorBusquedaNC, setValorBusquedaNC] = useState('');
  const [ncfNotaCredito, setNcfNotaCredito] = useState('');
  const [montoNotaCredito, setMontoNotaCredito] = useState('');
  const [notaCreditoInfo, setNotaCreditoInfo] = useState(null);
  const [buscandoNC, setBuscandoNC] = useState(false);

  const metodosConCasilla = useMemo(
    () => metodosUnicos.filter((m) => m.codigo !== 'OTROS'),
    [metodosUnicos]
  );

  useEffect(() => {
    if (open) {
      const primerMetodo =
        metodosUnicos.find((m) => m.codigo !== 'OTROS')?.codigo ||
        metodosUnicos[0]?.codigo ||
        'EFECTIVO';
      setMetodoPago(primerMetodo);
      const montosIniciales = {};
      const referenciasIniciales = {};
      metodosUnicos.forEach((metodo) => {
        montosIniciales[metodo.codigo] = metodo.codigo === primerMetodo ? parseFloat(total) || 0 : 0;
        referenciasIniciales[metodo.codigo] = '';
      });
      setMontos(montosIniciales);
      setReferencias(referenciasIniciales);
      setError('');
      setTipoBusquedaNC('ncf');
      setValorBusquedaNC('');
      setNcfNotaCredito('');
      setMontoNotaCredito('');
      setNotaCreditoInfo(null);
    }
  }, [open, total, metodosUnicos]);

  const handleCambioMetodo = (nuevoMetodo) => {
    setMetodoPago(nuevoMetodo);
    if (nuevoMetodo !== 'OTROS') {
      const totalNum = parseFloat(total) || 0;
      setMontos((prev) => {
        const next = {};
        metodosConCasilla.forEach((m) => {
          next[m.codigo] = m.codigo === nuevoMetodo ? totalNum : 0;
        });
        return { ...prev, ...next };
      });
    } else {
      setMontos((prev) => {
        const next = {};
        metodosConCasilla.forEach((m) => {
          next[m.codigo] = 0;
        });
        return { ...prev, ...next };
      });
    }
  };

  const buscarNotaCredito = async () => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const valor = (valorBusquedaNC || '').trim();
    if (!valor) {
      showError(
        `Ingrese el ${tipoBusquedaNC === 'ncf' ? 'NCF' : tipoBusquedaNC === 'ncred' ? 'Nº de Nota de Crédito' : 'Nº de Factura'}`
      );
      return;
    }
    const body = {
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '',
    };
    if (tipoBusquedaNC === 'ncf') body.ncf = valor;
    else if (tipoBusquedaNC === 'ncred') body.ncred = parseInt(valor, 10);
    else if (tipoBusquedaNC === 'nfact') body.nfact = parseInt(valor, 10);
    if (tipoBusquedaNC === 'ncred' && isNaN(body.ncred)) {
      showError('Debe ingresar un número de nota de crédito válido');
      return;
    }
    if (tipoBusquedaNC === 'nfact' && isNaN(body.nfact)) {
      showError('Debe ingresar un número de factura válido');
      return;
    }
    setBuscandoNC(true);
    setError('');
    try {
      const response = await fetch(servidor + '/api/facturabackend_buscarnotacredito_por_ncf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.encontrado) {
        setNotaCreditoInfo(data);
        setNcfNotaCredito(data.ncf || '');
        setMontoNotaCredito(data.disponible > 0 ? String(data.disponible) : '0');
        if (data.ya_aplicada) showError('Esta nota de crédito ya fue aplicada a otra factura.');
        if (!data.ya_aplicada && data.disponible > 0) {
          setMontos((prev) => {
            const next = {};
            metodosConCasilla.forEach((m) => {
              next[m.codigo] = 0;
            });
            return { ...prev, ...next };
          });
        }
      } else {
        setNotaCreditoInfo(null);
        setNcfNotaCredito('');
        setMontoNotaCredito('');
        showError(data.Error || 'No se encontró nota de crédito con ese criterio');
      }
    } catch {
      setNotaCreditoInfo(null);
      setNcfNotaCredito('');
      showError('Error de conexión al buscar la nota de crédito');
    } finally {
      setBuscandoNC(false);
    }
  };

  const montoNC = parseFloat(montoNotaCredito) || 0;
  const totalPagado =
    metodosConCasilla.reduce((sum, m) => sum + (parseFloat(montos[m.codigo]) || 0), 0) + montoNC;

  const totalFactura = parseFloat(total) || 0;
  const diferencia = totalPagado - totalFactura;

  const handleConfirmar = () => {
    if (!puedeModificar) {
      setError('No tiene permiso para registrar pagos.');
      return;
    }
    if (totalFactura <= 0) {
      setError('El total de la factura debe ser mayor a 0.');
      return;
    }
    if (totalPagado < totalFactura) {
      setError('El monto pagado no puede ser menor al total de la factura.');
      return;
    }

    const pagos = {};
    metodosConCasilla.forEach((metodo) => {
      const monto = parseFloat(montos[metodo.codigo]) || 0;
      const referencia = referencias[metodo.codigo] || '';
      if (monto > 0) {
        pagos[String(metodo.codigo || '').toLowerCase()] = {
          monto,
          referencia,
          metodo: metodo.nombre,
          documento: metodo.original || metodo.nombre,
          cuenta_contable: metodo.cuenta_contable || '',
        };
      }
    });

    if (montoNC > 0 && (ncfNotaCredito || '').trim()) {
      if (!notaCreditoInfo || notaCreditoInfo.ya_aplicada) {
        setError('Debe buscar una nota de crédito válida antes de aplicar.');
        return;
      }
      if (montoNC > (notaCreditoInfo.disponible || 0)) {
        setError(
          `El monto a aplicar no puede superar el disponible ($${(notaCreditoInfo.disponible || 0).toFixed(2)}).`
        );
        return;
      }
      pagos.nota_credito = {
        monto: montoNC,
        ncf: (ncfNotaCredito || '').trim(),
        metodo: 'Nota de crédito',
      };
    }

    const datosPago = {
      metodoPrincipal: metodoPago,
      totalFactura,
      totalPagado,
      pagos,
    };

    onConfirm && onConfirm(datosPago);
  };

  const actualizarMonto = (codigo, valor) => {
    setMontos((prev) => ({ ...prev, [codigo]: valor }));
  };

  const actualizarReferencia = (codigo, valor) => {
    setReferencias((prev) => ({ ...prev, [codigo]: valor }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          mt: 6,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.accentBlueSoft,
            color: FP.color.accentBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PaymentIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
          Pago de la factura
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.surfaceAlt,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: FP.color.textSecondary,
              }}
            >
              Total factura
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.6rem',
                lineHeight: 1.1,
                mt: 0.5,
                color: FP.color.textPrimary,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
              }}
            >
              ${totalFactura.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: FP.color.textSecondary,
              }}
            >
              Total pagado
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.3rem',
                lineHeight: 1.1,
                mt: 0.5,
                color: FP.color.textPrimary,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              ${totalPagado.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: FP.color.textSecondary,
              }}
            >
              {diferencia >= 0 ? 'Cambio' : 'Faltante'}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.3rem',
                lineHeight: 1.1,
                mt: 0.5,
                color: diferencia >= 0 ? FP.color.successDark : FP.color.danger,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              ${Math.abs(diferencia).toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Método principal"
              value={metodoPago}
              onChange={(e) => handleCambioMetodo(e.target.value)}
              disabled={!puedeModificar}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            >
              {metodosUnicos.map((metodo) => (
                <MenuItem key={metodo.codigo} value={metodo.codigo}>
                  {metodo.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Total pagado"
              value={totalPagado.toFixed(2)}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1, borderColor: FP.color.border }} />
            <Typography
              sx={{
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: FP.color.textSecondary,
              }}
            >
              <NotaCreditoIcon fontSize="small" /> Aplicar nota de crédito
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Buscar por"
              value={tipoBusquedaNC}
              onChange={(e) => setTipoBusquedaNC(e.target.value)}
              disabled={!puedeModificar}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            >
              <MenuItem value="ncf">NCF</MenuItem>
              <MenuItem value="ncred">Nº Nota de Crédito</MenuItem>
              <MenuItem value="nfact">Nº Factura (origen)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label={
                tipoBusquedaNC === 'ncf'
                  ? 'NCF'
                  : tipoBusquedaNC === 'ncred'
                  ? 'Nº Nota de Crédito'
                  : 'Nº Factura'
              }
              value={valorBusquedaNC}
              onChange={(e) => setValorBusquedaNC(e.target.value)}
              placeholder={tipoBusquedaNC === 'ncf' ? 'Ej: E3300000001' : 'Ej: 123'}
              disabled={!puedeModificar}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={buscarNotaCredito}
              disabled={buscandoNC || !(valorBusquedaNC || '').trim() || !puedeModificar || !usuarioLog}
              sx={{
                height: 46,
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.6,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              {buscandoNC ? 'Buscando…' : 'Buscar'}
            </Button>
          </Grid>
          {notaCreditoInfo && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                  Nº N/C: <strong>{notaCreditoInfo.ncred}</strong>
                  {notaCreditoInfo.ya_aplicada
                    ? ' (ya aplicada)'
                    : ` | Disponible: $${(notaCreditoInfo.disponible || 0).toFixed(2)}`}
                </Typography>
              </Grid>
              {!notaCreditoInfo.ya_aplicada && notaCreditoInfo.disponible > 0 && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto a aplicar (Nota de Crédito)"
                    type="number"
                    value={montoNotaCredito}
                    onChange={(e) => setMontoNotaCredito(e.target.value)}
                    inputProps={{ min: 0, max: notaCreditoInfo.disponible, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    helperText={`Máximo: $${(notaCreditoInfo.disponible || 0).toFixed(2)}`}
                    disabled={!puedeModificar}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1, borderColor: FP.color.border }} />
          </Grid>

          {(metodoPago === 'OTROS'
            ? metodosConCasilla
            : metodosConCasilla.filter((m) => m.codigo === metodoPago)
          ).map((metodo) => (
            <React.Fragment key={metodo.codigo}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={metodo.nombre}
                  type="number"
                  value={montos[metodo.codigo] ?? ''}
                  onChange={(e) => actualizarMonto(metodo.codigo, e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText={metodoPago === 'OTROS' ? 'Sume los montos para cuadrar con el total' : undefined}
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              {(metodo.necesita_referencia ||
                metodo.codigo === 'TARJETA' ||
                metodo.codigo === 'TRANSFERENCIA' ||
                metodo.codigo === 'CHEQUE') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={`Referencia ${metodo.nombre.toLowerCase()}`}
                    value={referencias[metodo.codigo] || ''}
                    onChange={(e) => actualizarReferencia(metodo.codigo, e.target.value)}
                    placeholder={`Ingrese referencia para ${metodo.nombre}`}
                    disabled={!puedeModificar}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
              )}
            </React.Fragment>
          ))}

          {error && (
            <Grid item xs={12}>
              <Alert
                severity="error"
                sx={{ borderRadius: 2, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.dangerSoft }}
              >
                {error}
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: FP.color.textSecondary,
            borderRadius: 1.4,
            '&:hover': { bgcolor: FP.color.surfaceAlt },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmar}
          disabled={!puedeModificar}
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
          }}
        >
          Confirmar pago
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PagoFactura;
